import type { ApolloError } from '@apollo/client';
import { useMutation } from '@apollo/client';

import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import SimpleTabs from '../../components/UI/SimpleTabs';
import { apiRoutes } from '../../constants/api-routes.constants';
import { ARIA_LABELS } from '../../constants/ariaLabels.constants';
import { AUTH_CONSTANTS } from '../../constants/auth.constants';
import { sectionIDs } from '../../constants/section-ids.constants';
import { USER } from '../../constants/user.constants';
import { DELETE_USER_MUTATION } from '../../graphql/resolvers/userMutations/deleteUser.gql';
import { exportUserData } from '../../helpers/exportUserData';
import type { TabConfig } from '../../interfaces/globals.interface';
import type { UserSliceState } from '../../interfaces/user.interface';
import { userSlice } from '../../store/user.store';
import ButtonComponent from '../UI/Button/Button.component';
import { SimpleDialog } from '../UI/SimpleDialog';
import { AccountView } from './AccountView';
import { BillingView } from './BillingView';
import { DevicesView } from './DevicesView';
import { IntegrationsView } from './IntegrationsView';
import { PreferencesView } from './PreferencesView';
import './user-settings.scss';

interface UserState {
  userStore: UserSliceState;
}

/**
 * Renders the user settings page content (four tabs).
 */
export const UserSettings = ({
  username,
}: {
  username: string;
}): React.ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const hashSlug = location.hash.replace(/^#/, '') || undefined;
  const dispatch = useDispatch();
  const userStore = useSelector((state: UserState) => state.userStore.user);
  const userAccountId = userStore.id;
  const [refreshToken, setRefreshToken] = useState('');
  const [open, setOpen] = useState(false);
  const REFRESH_TOKEN = localStorage.getItem(AUTH_CONSTANTS.JWT_REFRESH_TOKEN);
  const { DELETE_ACCOUNT_INIT, DELETE_ACCOUNT_SUCCESS } = userSlice.actions;

  useEffect(() => {
    setRefreshToken(REFRESH_TOKEN as string);
  }, [REFRESH_TOKEN]);

  const [deleteUser, { loading, error }] = useMutation(DELETE_USER_MUTATION, {
    variables: {
      input: {
        userId: userAccountId,
        refreshToken,
      },
    },
    onCompleted: async ({ deleteUser }) => {
      const deleteUserMessage = await deleteUser.message;
      if (deleteUserMessage !== null) {
        localStorage.removeItem(AUTH_CONSTANTS.JWT_TOKEN);
        localStorage.removeItem(AUTH_CONSTANTS.JWT_REFRESH_TOKEN);
        navigate(apiRoutes.LOGIN);
        // @ts-ignore
        dispatch(DELETE_ACCOUNT_SUCCESS(true));
      } else {
        throw new Error('deleteUserMessage is null');
      }
    },
    onError: (ex: ApolloError) => {
      console.error(ex, error);
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDeleteAccount = async () => {
    handleClose();
    // @ts-ignore
    dispatch(DELETE_ACCOUNT_INIT(true));
    return await deleteUser({
      variables: { input: { userId: userAccountId, refreshToken } },
    });
  };

  const handleDownloadBeforeDelete = async () => {
    try {
      await exportUserData(username);
    } catch (e) {
      console.error('Data export failed', e);
    }
  };

  const tabs: TabConfig[] = [
    {
      label: USER.TAB_LABELS.PREFERENCES,
      child: <PreferencesView />,
      slug: 'preferences',
    },
    {
      label: USER.TAB_LABELS.ACCOUNT,
      child: (
        <AccountView handleClickOpen={handleClickOpen} username={username} />
      ),
      slug: 'account',
    },
    {
      label: USER.TAB_LABELS.DEVICES,
      child: <DevicesView />,
      slug: 'devices',
    },
    {
      label: USER.TAB_LABELS.INTEGRATIONS,
      child: <IntegrationsView username={username} />,
      slug: 'integrations',
    },
    {
      label: USER.TAB_LABELS.BILLING,
      child: <BillingView username={username} />,
      slug: 'billing',
    },
  ];

  return (
    <>
      {loading ? (
        <div className="LoadingSpinner" />
      ) : (
        <section className="APP__User__Settings" id={sectionIDs.USER_SETTINGS}>
          <SimpleTabs
            ariaLabel={ARIA_LABELS.USER_SETTINGS}
            tabs={tabs}
            tabsGroupName="user-settings"
            parentComponent={'user-settings'}
            defaultSlug={hashSlug}
          />
          <SimpleDialog
            closeModalLabel={USER.DELETE_ACCOUNT_CLOSE_DIALOG}
            actionLabel={USER.DELETE_ACCOUNT}
            dialogBody={
              <>
                <p>{USER.DELETE_ACCOUNT_BODY}</p>
                <ButtonComponent
                  type="button"
                  variant="outline"
                  onClick={handleDownloadBeforeDelete}
                >
                  Download my data first
                </ButtonComponent>
              </>
            }
            dialogTitle={USER.DELETE_ACCOUNT_CONFIRM}
            open={open}
            onClose={handleClose}
            onSubmit={handleDeleteAccount}
          />
        </section>
      )}
    </>
  );
};
